import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, Stack, Switch,
  TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { apiGetAll } from 'api/service';
import { downloadAuthed } from 'api/download';
import { GREEN, MONO, ago } from './scanUi';
import { KINDS, aboutToGo, expiry, filterArtifacts, kindCount, kindOf, newestFirst, purged, size, whyLine } from './artifactLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const PAGE = 50;

export default function ArtifactRegistryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [note, setNote] = useState(null);

  useEffect(() => {
    apiGetAll('/api/scan-artifacts/').then((d) => setRows(newestFirst(d))).catch(() => setError('Could not load the evidence.')).finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => filterArtifacts(rows, { kind, q, showDeleted }), [rows, kind, q, showDeleted]);
  const soon = useMemo(() => aboutToGo(rows), [rows]);
  const fetch = (a) => downloadAuthed(a.download_url).catch((e) => setError(e.message));

  const action = (a) => {
    if (purged(a)) return <Typography variant="caption" color="text.secondary">Deleted</Typography>;
    if (a.download_url) return <Button size="small" variant="outlined" color="inherit" onClick={() => fetch(a)}>Download</Button>;
    if (a.external_url) return <Button size="small" variant="outlined" color="inherit" component="a" href={a.external_url} target="_blank" rel="noreferrer">Open link</Button>;
    if (a.text_content) return <Button size="small" variant="outlined" color="inherit" onClick={() => setNote(a)}>Read</Button>;
    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700}>Evidence</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>The raw files behind every scan, with a plain line saying why each one is here</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN }} /></Box>}

      {!loading && soon.length > 0 && (
        <Box sx={{ borderRadius: 3, border: 1, borderColor: alpha('#f59e0b', 0.4), bgcolor: alpha('#f59e0b', 0.06), p: 2.5, mb: 2.5 }}>
          <Typography fontWeight={700} sx={{ color: '#fcd34d', mb: 1 }}>About to be deleted</Typography>
          {soon.map((a) => (
            <Stack key={a.id} direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ py: 1, borderTop: 1, borderColor: alpha('#f59e0b', 0.2) }}>
              <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 13.5, flex: '1 1 200px' }} noWrap>{a.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: '2 1 260px' }} noWrap>{whyLine(a)}</Typography>
              <Typography variant="body2" sx={{ color: '#fcd34d', fontWeight: 600, width: 130 }}>{expiry(a).text}</Typography>
              {action(a)}
            </Stack>
          ))}
        </Box>
      )}

      {!loading && (
        <>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_, v) => { if (v) { setKind(v); setLimit(PAGE); } }}>
              {KINDS.map((k) => <ToggleButton key={k.id} value={k.id} sx={{ textTransform: 'none', px: 1.8, borderRadius: '999px !important', fontWeight: 600 }}>{k.label} {kindCount(rows, k.id, showDeleted)}</ToggleButton>)}
            </ToggleButtonGroup>
            <FormControlLabel sx={{ ml: 1 }} label={<Typography variant="body2" color="text.secondary">Show deleted</Typography>} control={<Switch size="small" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />} />
            <TextField size="small" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by scan, address or file" sx={{ ml: 'auto', minWidth: 280 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>

          <Box sx={{ ...panel, overflow: 'hidden' }}>
            {!shown.length && <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>{rows.length ? 'No file matches.' : 'No evidence yet. Files appear here when a scan finishes.'}</Typography>}
            {shown.slice(0, limit).map((a) => {
              const k = kindOf(a);
              const ex = expiry(a);
              const gone = purged(a);
              return (
                <Stack key={a.id} direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ px: 2.5, py: 1.8, borderTop: 1, borderColor: 'divider', opacity: gone ? 0.55 : 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.2, py: 0.5, borderRadius: 1.5, width: 66, textAlign: 'center', bgcolor: alpha(k.color || '#94a3b8', 0.16), color: k.color || '#94a3b8' }}>{(k.id === 'all' ? 'OTHER' : k.label.replace(/s$/, '')).toUpperCase()}</Typography>
                  <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 14.5 }} noWrap>{a.label}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{whyLine(a)} · {ago(a.created_at)}</Typography>
                  </Box>
                  <Box sx={{ width: 150 }}>
                    <Typography variant="caption" color="text.secondary">{ex.text}</Typography>
                    <Box sx={{ height: 5, borderRadius: 1, bgcolor: alpha('#fff', 0.08), mt: 0.5 }}><Box sx={{ height: '100%', width: `${ex.pct}%`, borderRadius: 1, bgcolor: GREEN }} /></Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 64, textAlign: 'right', fontFamily: MONO }}>{size(a.size_bytes)}</Typography>
                  <Box sx={{ minWidth: 96, textAlign: 'right' }}>{action(a)}</Box>
                </Stack>
              );
            })}
            {shown.length > limit && <Button fullWidth onClick={() => setLimit((n) => n + PAGE)} sx={{ py: 1.5, color: GREEN, fontWeight: 700, borderTop: 1, borderColor: 'divider', borderRadius: 0 }}>Show more ({shown.length - limit} left)</Button>}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.6 }}>
            Each file has a fingerprint so you can show it was not changed. Files are deleted when their keeping time ends, and the record stays so you can see it once existed.
          </Typography>
        </>
      )}

      <Dialog open={!!note} onClose={() => setNote(null)} fullWidth maxWidth="md">
        {note && <>
          <DialogTitle>{note.label}</DialogTitle>
          <DialogContent><Box component="pre" sx={{ m: 0, fontFamily: MONO, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.text_content}</Box></DialogContent>
          <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setNote(null)}>Close</Button></DialogActions>
        </>}
      </Dialog>
    </Box>
  );
}
